import React, { useState, useEffect, useCallback } from 'react';
import { 
  Trophy, 
  Target, 
  Award, 
  Sparkles, 
  CheckCircle2, 
  X, 
  Zap, 
  Star 
} from 'lucide-react';
import { useThemeTokens } from '../hooks/useActiveTheme';

export type ToastType = 'first_quiz' | 'skill_mastered' | 'achievement' | 'info' | 'success';

export interface ToastOptions {
  id?: string;
  title: string;
  message: string;
  type?: ToastType;
  duration?: number; // ms, defaults to 5000
}

export interface ToastItem extends Required<ToastOptions> {
  createdAt: number;
}

const TOAST_EVENT = 'show-app-toast';

/**
 * Global helper to trigger a toast notification from anywhere in the app
 */
export function triggerToast(options: ToastOptions) {
  const event = new CustomEvent(TOAST_EVENT, { detail: options });
  window.dispatchEvent(event);
}

/**
 * Helper to check and trigger the "First Quiz of the Day" toast notification.
 * Returns true if triggered (i.e. first quiz today), false otherwise.
 */
export function checkAndTriggerFirstQuizOfDayToast(): boolean {
  try {
    const today = new Date().toISOString().split('T')[0];
    const lastQuizDate = localStorage.getItem('last_quiz_completed_date');
    if (lastQuizDate !== today) {
      localStorage.setItem('last_quiz_completed_date', today);
      triggerToast({
        title: 'First Quiz of the Day! 🎯',
        message: 'Great start to your study session today! Daily momentum keeps you on track.',
        type: 'first_quiz',
        duration: 5500
      });
      return true;
    }
  } catch (err) {
    console.warn('Unable to access localStorage for quiz streak check', err);
  }
  return false;
}

/**
 * Helper to trigger the "Skill Topic Mastered" toast notification.
 */
export function triggerSkillMasteredToast(skillName: string) {
  triggerToast({
    title: 'Skill Topic Mastered! 🏆',
    message: `You completed and mastered "${skillName}". +50 XP added to your progress!`,
    type: 'skill_mastered',
    duration: 5500
  });
}

export const ToastNotificationContainer: React.FC = () => {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const tokens = useThemeTokens();

  const removeToast = useCallback((id: string) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  useEffect(() => {
    const handleEvent = (e: Event) => {
      const customEvent = e as CustomEvent<ToastOptions>;
      if (!customEvent.detail) return;

      const newToast: ToastItem = {
        id: customEvent.detail.id || `toast_${Date.now()}_${Math.random().toString(36).substring(2, 6)}`,
        title: customEvent.detail.title,
        message: customEvent.detail.message,
        type: customEvent.detail.type || 'achievement',
        duration: customEvent.detail.duration || 5000,
        createdAt: Date.now()
      };

      setToasts(prev => {
        // Keep at most 3 toasts on screen at once
        const next = [...prev, newToast];
        if (next.length > 3) return next.slice(next.length - 3);
        return next;
      });
    };

    window.addEventListener(TOAST_EVENT, handleEvent);
    return () => window.removeEventListener(TOAST_EVENT, handleEvent);
  }, []);

  if (toasts.length === 0) return null;

  return (
    <div 
      className="fixed bottom-5 right-5 sm:bottom-6 sm:right-6 z-[9999] flex flex-col gap-3 max-w-sm sm:max-w-md w-[calc(100vw-2.5rem)] pointer-events-none"
      aria-live="polite"
      aria-atomic="true"
      role="region"
      aria-label="Notification Center"
    >
      {toasts.map(toast => (
        <SingleToastItem key={toast.id} toast={toast} onDismiss={() => removeToast(toast.id)} tokens={tokens} />
      ))}
    </div>
  );
};

interface SingleToastItemProps {
  toast: ToastItem;
  onDismiss: () => void;
  tokens: ReturnType<typeof useThemeTokens>;
}

const SingleToastItem: React.FC<SingleToastItemProps> = ({ toast, onDismiss, tokens }) => {
  const [progress, setProgress] = useState(100);

  useEffect(() => {
    const startTime = Date.now();
    const interval = setInterval(() => {
      const elapsed = Date.now() - startTime;
      const remaining = Math.max(0, 100 - (elapsed / toast.duration) * 100);
      setProgress(remaining);
      if (remaining <= 0) {
        clearInterval(interval);
        onDismiss();
      }
    }, 50);

    return () => clearInterval(interval);
  }, [toast.duration, onDismiss]);

  const getToastIcon = () => {
    switch (toast.type) {
      case 'first_quiz':
        return <Target className="w-5 h-5 text-amber-500 animate-pulse" />;
      case 'skill_mastered':
        return <Trophy className="w-5 h-5 text-emerald-500 animate-bounce" />;
      case 'achievement':
        return <Award className="w-5 h-5 text-indigo-500" />;
      case 'success':
        return <CheckCircle2 className="w-5 h-5 text-emerald-500" />;
      case 'info':
      default:
        return <Sparkles className="w-5 h-5 text-sky-500" />;
    }
  };

  const getTypeBadge = () => {
    switch (toast.type) {
      case 'first_quiz':
        return 'Daily Streak';
      case 'skill_mastered':
        return 'Mastery Unlocked';
      case 'achievement':
        return 'Achievement';
      case 'success':
        return 'Completed';
      case 'info':
      default:
        return 'Notification';
    }
  };

  // Centralized theme adaptation
  const getBorderColor = () => {
    if (tokens.isHighContrast) return 'border-2 border-black';
    if (tokens.isWarmAcademic) return 'border border-[#0D6E6E]/30';
    if (tokens.isClinical) return 'border border-teal-500/30';
    if (tokens.isVibrant) return 'border border-violet-500/30';
    if (tokens.isStructuredCourse) return 'border border-blue-500/30';
    return 'border border-slate-200 dark:border-slate-800';
  };

  return (
    <div 
      className={`pointer-events-auto relative overflow-hidden rounded-2xl md:rounded-3xl shadow-2xl backdrop-blur-md transition-all duration-300 animate-pop p-4 md:p-5 flex items-start gap-3.5 bg-white/95 dark:bg-slate-900/95 ${getBorderColor()}`}
      role="alert"
    >
      {/* Icon Badge */}
      <div className={`p-2.5 rounded-xl md:rounded-2xl shrink-0 flex items-center justify-center shadow-xs ${
        toast.type === 'first_quiz' 
          ? 'bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-800/50' 
          : toast.type === 'skill_mastered' 
          ? 'bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-200 dark:border-emerald-800/50' 
          : tokens.badge
      }`}>
        {getToastIcon()}
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0 pr-2">
        <div className="flex items-center gap-2 mb-1">
          <span className={`text-[9px] font-black uppercase tracking-widest px-2 py-0.5 rounded-md ${
            toast.type === 'first_quiz' 
              ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/50 dark:text-amber-200 font-extrabold'
              : toast.type === 'skill_mastered'
              ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/50 dark:text-emerald-200 font-extrabold'
              : tokens.badge
          }`}>
            {getTypeBadge()}
          </span>
        </div>

        <h4 className={`text-xs md:text-sm font-extrabold text-slate-900 dark:text-slate-100 leading-tight mb-1 truncate ${tokens.headerFont}`}>
          {toast.title}
        </h4>

        <p className="text-[11px] md:text-xs text-slate-600 dark:text-slate-300 font-medium leading-relaxed break-words">
          {toast.message}
        </p>
      </div>

      {/* Close Button */}
      <button
        onClick={onDismiss}
        className="p-1 text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 transition-colors rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 shrink-0"
        aria-label="Dismiss notification"
      >
        <X size={16} />
      </button>

      {/* Non-intrusive Progress Bar */}
      <div className="absolute bottom-0 left-0 right-0 h-1 bg-slate-100 dark:bg-slate-800 overflow-hidden">
        <div 
          className={`h-full transition-all duration-75 linear ${
            toast.type === 'first_quiz'
              ? 'bg-amber-500'
              : toast.type === 'skill_mastered'
              ? 'bg-emerald-500'
              : tokens.progressBar
          }`}
          style={{ width: `${progress}%` }}
        />
      </div>
    </div>
  );
};
